import { useMemo } from 'react';
import { AlertCircle } from 'lucide-react';

import { validateChannelCompatibility } from '@/lib/channelCapabilities';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

const ChannelSelectionDialog = ({
  isOpen,
  onOpenChange,
  tempChannels,
  handleChannelToggle,
  channelIcons,
  onSave,
  // Props for validation
  trigger = [],
  nodes = [],
}) => {
  // Validate compatibility whenever tempChannels changes
  const { isValid, issues } = useMemo(
    () => validateChannelCompatibility(trigger, nodes, tempChannels),
    [trigger, nodes, tempChannels]
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>Select Channels</DialogTitle>
          <DialogDescription>
            Choose the social media channels where this workflow will be active.
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-4 py-4'>
          <div className='space-y-2'>
            <Label>Available Channels</Label>
            <div className='grid grid-cols-2 gap-3'>
              {Object.entries(channelIcons).map(
                ([channelId, { icon, label }]) => (
                  <Button
                    key={channelId}
                    variant={
                      tempChannels.includes(channelId) ? 'default' : 'outline'
                    }
                    onClick={() => handleChannelToggle(channelId)}
                    className={`flex h-auto w-full items-center justify-start gap-3 p-4 transition-colors ${
                      tempChannels.includes(channelId)
                        ? 'bg-primary hover:bg-primary/90'
                        : 'hover:bg-muted'
                    }`}
                  >
                    <div className='flex h-10 w-10 items-center justify-center'>
                      {icon}
                    </div>
                    <div className='flex flex-col items-start'>
                      <span className='font-medium'>{label}</span>
                    </div>
                  </Button>
                )
              )}
            </div>
          </div>

          {/* Compatibility Warning */}
          {!isValid && issues.length > 0 && (
            <Alert variant='destructive'>
              <AlertCircle className='h-4 w-4' />
              <AlertTitle>Compatibility Issue</AlertTitle>
              <AlertDescription>
                <p className='mb-2'>
                  The following steps are not supported by all selected
                  channels:
                </p>
                <ul className='list-inside list-disc text-sm'>
                  {issues.map((issue, idx) => (
                    <li key={idx}>
                      <span className='font-medium'>{issue.label}</span> (
                      {issue.type}) - not available on{' '}
                      {issue.unsupportedChannels.join(', ')}
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={!isValid}>
            {tempChannels.length > 0
              ? `Save ${tempChannels.length} Channel${
                  tempChannels.length > 1 ? 's' : ''
                }`
              : 'Save Channels'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChannelSelectionDialog;
